import type { AddressDTO, ContactAddressDTO } from '@invenco/common-interface/shared';

import { Skeleton } from '@/components/core/skeleton';

type Props = {
  address?: AddressDTO | ContactAddressDTO;
  isLoading?: boolean;
  compact?: boolean;
};

function formatLocation(address?: AddressDTO | ContactAddressDTO) {
  const cityState = [address?.city, address?.state].filter(Boolean).join(', ');

  return [cityState, address?.postcode].filter(Boolean).join(' ');
}

export function AddressDisplay({ address, isLoading, compact }: Props) {
  if (isLoading) {
    return (
      <div className="space-y-1" aria-busy>
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  const location = formatLocation(address);

  // Read each contact field independently: a caller may supply some fields (e.g. a
  // differing email) without a contactName, so the rows must not all hinge on the name.
  const contact: Partial<ContactAddressDTO> = address ?? {};

  const contactRows = [contact.contactName, contact.contactNumber, contact.contactEmail];

  const addressLines = [address?.address1, address?.address2, location, address?.country];

  const rows = compact
    ? [...contactRows, [address?.companyName, ...addressLines].filter(Boolean).join(', ')].filter(
        Boolean,
      )
    : [address?.companyName, ...contactRows, ...addressLines].filter(Boolean);

  return (
    <div className="space-y-1 text-sm">
      {rows.map((row) => (
        <div key={row}>{row}</div>
      ))}
    </div>
  );
}
